import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, set } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { ArrowLeft, Monitor, Wand2, ScanText } from 'lucide-react';
import PopulateValuesModal from './PopulateValuesModal';
import OcrFillModal from './OcrFillModal';
import ConfirmDialog from './ConfirmDialog';
import { compressImage } from './imageCompress';
import './admin-page.css';

const FIELDS = [
  { key: 'Name', label: 'Product Name', placeholder: 'e.g. Dell Latitude 5490' },
  { key: 'Price', label: 'Price (₹)', placeholder: 'e.g. 18000', type: 'number' },
  { key: 'Processor', label: 'Processor', placeholder: 'e.g. Intel Core i5-8250U' },
  { key: 'ClockSpeed', label: 'CPU Clock Speed', placeholder: 'e.g. Base 2.5GHz / Turbo 4.6GHz' },
  { key: 'Ram', label: 'RAM', placeholder: 'e.g. 8GB DDR4' },
  { key: 'Storage', label: 'Storage', placeholder: 'e.g. 256GB SSD' },
  { key: 'Display', label: 'Display', placeholder: 'e.g. 14" FHD 1920x1080' },
  { key: 'RefreshRate', label: 'Refresh Rate', placeholder: 'e.g. 144Hz' },
  { key: 'GPU', label: 'GPU', placeholder: 'e.g. Intel UHD 620' },
  { key: 'Battery', label: 'Battery', placeholder: 'e.g. 4-cell 65Wh' },
  { key: 'OS', label: 'Operating System', placeholder: 'e.g. Windows 10 Pro' },
];

// Scraped spec labels are free text with arbitrarily many sub-rows per
// category (e.g. "Processor Brand" / "Processor Name" / "Processor Variant"
// / "Clock Speed" all belong in the one Processor field) — matched against
// these fixed form fields by tokenizing each label into whole words and
// checking word-stem matches, then COLLECTING every matching spec for a
// field and joining them, rather than picking one "primary" value and
// dropping the rest.
//
// Word-boundary tokenization (not raw substring search) matters here:
// "Touchscreen" must not match the "screen" keyword (it's a different word),
// while "Graphics" must still match the "graphic" stem (prefix match).
// `exclude` stops cross-contamination between similarly-worded fields, e.g.
// "Dedicated Graphic Memory Capacity" contains "memory" but is GPU VRAM, not
// system RAM.
const FIELD_RULES = {
  // Processor holds only the CPU identity now — clock/frequency rows are routed
  // to their own ClockSpeed field, so they're excluded here to avoid duplication.
  Processor: { include: [['processor'], ['cpu'], ['chipset']], exclude: ['graphic', 'gpu', 'clock', 'frequency', 'turbo', 'speed'] },
  // CPU clock/frequency only — must not absorb RAM frequency, GPU clocks, bus
  // speed, or the display's refresh rate (all of which also read as "frequency").
  ClockSpeed: { include: [['clock'], ['frequency'], ['turbo']], exclude: ['ram', 'memory', 'graphic', 'gpu', 'video', 'refresh', 'display', 'screen', 'bus', 'storage'] },
  Ram: { include: [['ram'], ['memory']], exclude: ['graphic', 'gpu', 'video'] },
  Storage: { include: [['storage'], ['hard', 'disk'], ['ssd'], ['hdd'], ['capacity']], exclude: ['ram', 'memory', 'graphic'] },
  Display: { include: [['display'], ['screen'], ['monitor'], ['resolution']], exclude: ['touch', 'refresh'] },
  RefreshRate: { include: [['refresh']], exclude: [] }, // "Refresh Rate" → 144Hz etc.
  GPU: { include: [['graphic'], ['gpu']] }, // "graphic" stem also matches "graphics"
  Battery: { include: [['battery']] },
  OS: { include: [['operating', 'system'], ['os']] },
};

// Dropped when something more informative is also present for the same
// field (e.g. "SSD: Yes" once "SSD Capacity: 512GB" is also known) — kept
// only as a last resort if nothing else matched.
const SKIP_VALUES = new Set(['yes', 'no', 'n/a', 'na', '-', '—']);

function tokenize(label) {
  return String(label).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function hasWord(tokens, stem) {
  return tokens.some(t => t.startsWith(stem));
}

function fieldMatches(tokens, rule) {
  if (rule.exclude && rule.exclude.some(stem => hasWord(tokens, stem))) return false;
  return rule.include.some(group => group.every(stem => hasWord(tokens, stem)));
}

function mapSpecsToFields(specs) {
  const collected = {};
  (specs || []).forEach(({ label, value }) => {
    const tokens = tokenize(label);
    for (const [field, rule] of Object.entries(FIELD_RULES)) {
      if (fieldMatches(tokens, rule)) {
        (collected[field] = collected[field] || []).push(String(value).trim());
        break; // a label only ever contributes to the first field it matches
      }
    }
  });

  const mapped = {};
  for (const [field, values] of Object.entries(collected)) {
    const meaningful = values.filter(v => !SKIP_VALUES.has(v.toLowerCase()));
    const list = meaningful.length > 0 ? meaningful : values;
    const seen = new Set();
    mapped[field] = list.filter(v => {
      const k = v.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).join(' ');
  }
  return mapped;
}

// Guards the rule-based fallback against mislabeled OCR rows: a value made up
// only of spec-LABEL words with no number/brand (e.g. "Processor Name Clock
// Speed") is junk and must never land in a field.
const LABEL_WORDS = new Set(['processor', 'name', 'clock', 'speed', 'brand', 'variant',
  'type', 'memory', 'capacity', 'graphic', 'graphics', 'dedicated', 'ram', 'ssd', 'hdd',
  'storage', 'display', 'screen', 'resolution', 'operating', 'system', 'series', 'model',
  'number', 'generation', 'frequency', 'cache', 'core', 'cores', 'dimensions', 'weight',
  'color', 'colour', 'size', 'rate', 'refresh', 'battery', 'gpu', 'cpu']);
function isLabelJunk(v) {
  const s = String(v || '').trim();
  if (!s || /\d/.test(s)) return false;          // anything with a number is real
  const words = s.toLowerCase().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.every(w => LABEL_WORDS.has(w));
}

// What the stage-2 AI verifier is asked to map onto — independent of the
// local rule-based FIELD_RULES below, used as a smarter primary source with
// the rules kept as a deterministic fallback for anything it leaves blank.
const AI_TARGET_FIELDS = [
  { key: 'Processor',  hint: 'CPU IDENTITY ONLY (no clock speed — that has its own field). Order: Brand + core tier + model number + generation. Model number comes right after the core tier, never at the end. Examples: "Intel Core i5-1335U 13th Gen", "AMD Ryzen 5 7530U".' },
  { key: 'ClockSpeed', hint: 'CPU clock/frequency, cleanly restructured into a canonical short form — NOT copied verbatim. If both a base and a boost/turbo value exist, output "Base X.XGHz / Turbo Y.YGHz" (e.g. raw "Base Frequency 2.5GHz upto max turbo 5.4GHz" → "Base 2.5GHz / Turbo 5.4GHz"). If only one value exists, output just that value with the GHz unit (e.g. raw "5GHZ" → "5GHz", raw "up to 4.6 GHz" → "4.6GHz"). Always normalise the unit to "GHz". Empty string if no CPU clock speed is present.' },
  { key: 'Ram',        hint: 'RAM capacity, type, and frequency only (e.g. 16GB DDR4 3200MHz). Never GPU VRAM or CPU clock speed.' },
  { key: 'Storage',    hint: 'Storage capacity and type (e.g. 512GB NVMe SSD or 1TB HDD)' },
  { key: 'Display',    hint: 'Screen SIZE and RESOLUTION and panel type ONLY (e.g. 14" 1920x1080 IPS). NEVER touchscreen capability, pointing devices, refresh rate, or any boolean Yes/No feature flag.' },
  { key: 'RefreshRate',hint: 'Display refresh rate normalised to just the number + "Hz" (e.g. raw "Refresh Rate: 144 Hz" → "144Hz", raw "60hz display" → "60Hz"). Strip every other word. Empty string if no refresh rate is mentioned.' },
  { key: 'GPU',        hint: 'Dedicated GPU name and VRAM (e.g. NVIDIA GeForce RTX 3050 4GB). Empty string for integrated-only graphics.' },
  { key: 'Battery',    hint: 'Battery cell count and/or capacity (e.g. 4-cell 65Wh)' },
  { key: 'OS',         hint: 'Operating system name and edition (e.g. Windows 11 Home)' },
];

function genID() {
  return 'pc' + Math.floor(1000 + Math.random() * 9000);
}

function genFilename(file) {
  const ext = file.name.split('.').pop();
  return `${Date.now()}_${Math.random().toString(36).substr(2, 5)}.${ext}`;
}

export default function AddPC() {
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [productPreviews, setProductPreviews] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPopulate, setShowPopulate] = useState(false);
  const [showOcr, setShowOcr] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const coverInputRef = useRef();
  const prodInputRef = useRef();

  const handleField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handlePopulate = (result) => {
    // AI-verified mapping (stage 2, cross-checked by a second model) wins
    // when present and non-empty; the local rule-based mapping is the
    // deterministic fallback for any field the AI left blank rather than guess.
    const ruleMapped = mapSpecsToFields(result.specs);
    const aiMapped = result.mappedFields || {};
    const mapped = {};
    for (const key of Object.keys(ruleMapped)) {
      const aiVal = aiMapped[key];
      if (typeof aiVal === 'string' && aiVal.trim()) {
        mapped[key] = aiVal.trim();
      } else if (!isLabelJunk(ruleMapped[key])) {
        mapped[key] = ruleMapped[key];   // deterministic fallback, but never label-junk
      }
    }
    for (const key of Object.keys(aiMapped)) {
      if (mapped[key] === undefined && typeof aiMapped[key] === 'string' && aiMapped[key].trim()) {
        mapped[key] = aiMapped[key].trim();
      }
    }
    setForm(f => ({
      ...f,
      ...(result.name ? { Name: result.name } : {}),
      ...mapped,
    }));
    if (result.coverImageFile) {
      setCoverFile(result.coverImageFile);
      setCoverPreview(URL.createObjectURL(result.coverImageFile));
    }
  };

  const handleCover = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleProductImages = (e) => {
    const files = Array.from(e.target.files);
    setProductFiles(prev => [...prev, ...files]);
    setProductPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeProductImage = (i) => {
    setProductFiles(prev => prev.filter((_, idx) => idx !== i));
    setProductPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const uploadFile = (file, path) => new Promise((resolve, reject) => {
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, file);
    task.on('state_changed',
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
    );
  });

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!form.Name || !form.Price) return setError('Name and Price are required.');
    if (!coverFile) return setError('Cover image is required.');
    setError('');
    setShowConfirm(true);
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setError('');
    setSuccess('');
    setUploading(true);
    try {
      const pcID = genID();
      const coverC = await compressImage(coverFile);
      const coverName = genFilename(coverC);
      await uploadFile(coverC, `Refurbished/${coverName}`);
      const imageNames = [];
      for (const file of productFiles) {
        const compressed = await compressImage(file);
        const name = genFilename(compressed);
        await uploadFile(compressed, `Refurbished/${name}`);
        imageNames.push(name);
      }
      await set(dbRef(database, `Refurbished/${pcID}`), {
        ...form,
        Price: Number(form.Price),
        Cover_image: coverName,
        Images: imageNames.join(','),
      });
      setSuccess('PC added successfully!');
      setForm({});
      setCoverFile(null);
      setCoverPreview(null);
      setProductFiles([]);
      setProductPreviews([]);
      setProgress(0);
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard')}><ArrowLeft size={18} /></button>
        <div>
          <h1 className="ap-title">Add Refurbished PC</h1>
          <p className="ap-subtitle">Fill in details and upload images</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}
      {success && <div className="ap-success">{success}</div>}

      <div className="ap-card">
        <form onSubmit={handlePreSubmit}>
          <div className="ap-row">
            {FIELDS.map(f => (
              <div className="ap-form-group" key={f.key}>
                <label className="ap-label">{f.label}</label>
                <input
                  className="ap-input"
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.key] || ''}
                  onChange={e => handleField(f.key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <p className="ap-section-title">Cover Image</p>
          <div className="ap-img-upload-area" onClick={() => coverInputRef.current.click()}>
            <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCover} />
            {coverPreview
              ? <img src={coverPreview} alt="cover" style={{ maxHeight: 120, borderRadius: 8 }} />
              : <><div style={{ fontSize: '1.8rem' }}>🖼️</div><div>Click to select cover image</div></>
            }
          </div>

          <p className="ap-section-title">Product Images</p>
          <div className="ap-img-upload-area" onClick={() => prodInputRef.current.click()}>
            <input ref={prodInputRef} type="file" accept="image/*" multiple onChange={handleProductImages} />
            <div style={{ fontSize: '1.8rem' }}>📷</div>
            <div>Click to add product images</div>
            <div className="ap-img-upload-hint">You can add multiple images</div>
          </div>
          {productPreviews.length > 0 && (
            <div className="ap-img-previews">
              {productPreviews.map((src, i) => (
                <div className="ap-img-thumb" key={i}>
                  <img src={src} alt={`prod-${i}`} />
                  <button type="button" className="ap-img-thumb-remove" onClick={() => removeProductImage(i)}>✕</button>
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="ap-progress-wrap" style={{ marginTop: 16 }}>
              <div className="ap-progress-label">Uploading… {progress}%</div>
              <div className="ap-progress-bar"><div className="ap-progress-fill" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          <div className="ap-btn-row">
            <button type="button" className="ap-btn ap-btn-ai" style={{ display:'flex', alignItems:'center', gap:6 }} onClick={() => setShowPopulate(true)}>
              <Wand2 size={15} /> AI auto fill
            </button>
            <button type="button" className="ap-btn ap-btn-ocr" style={{ display:'flex', alignItems:'center', gap:6 }} onClick={() => setShowOcr(true)}>
              <ScanText size={15} /> OCR Fill
            </button>
            <button type="submit" className="ap-btn ap-btn-primary" disabled={uploading} style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Monitor size={16} /> {uploading ? 'Uploading…' : 'Add PC'}
            </button>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => navigate('/admin/dashboard')}>Cancel</button>
          </div>
        </form>
      </div>

      {showPopulate && (
        <PopulateValuesModal onClose={() => setShowPopulate(false)} onApply={handlePopulate} targetFields={AI_TARGET_FIELDS} />
      )}

      {showOcr && (
        <OcrFillModal onClose={() => setShowOcr(false)} onApply={handlePopulate} />
      )}

      {showConfirm && (
        <ConfirmDialog
          title="Add this PC?"
          message={`"${form.Name}" — ₹${Number(form.Price).toLocaleString('en-IN')}`}
          confirmLabel="Yes, Add PC"
          confirmingLabel="Adding…"
          confirming={uploading}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}
